import { Button } from "@/components/ui/button";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState, useRef } from "react";
import { FiLoader, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { validateCouponCode } from "../../Redux/ReduxSlices/paymentSlice";
import { useDispatch } from "react-redux";

// const stripePromise = loadStripe(import.meta.env.VITE_BACKEND_STRIPE_TEST_PK);
const stripePromise = loadStripe(import.meta.env.VITE_BACKEND_STRIPE_LIVE_PK);

const API_BASE = import.meta.env.VITE_BACKEND_PROD_URL;

// const API_BASE = import.meta.env.VITE_BACKEND_LOCAL_URL;

function Checkout({
  subscriptionPk,
  currency = "usd",
  isModalOpen,
  setIsModalOpen,
  planPrice,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [clientSecret, setClientSecret] = useState(null);
  const [piId, setPiId] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [amount, setAmount] = useState(planPrice);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [couponCodeInput, setCouponCodeInput] = useState("");

  // Add ref for email timeout
  const emailTimeoutRef = useRef(null);

  // Check if JWT token is available
  const JWT = localStorage.getItem("token") || "";

  // Separate useEffect for planPrice and subscriptionPk without email dependency
  useEffect(() => {
    // Set amount from planPrice prop if available
    // if (planPrice) {
    //   setAmount(planPrice);
    // }

    if (subscriptionPk) {
      // Force the createPaymentIntent to run on the next tick to ensure all state is properly set
      setTimeout(() => {
        createPaymentIntent();
      }, 0);
    } else if (isModalOpen && !subscriptionPk) {
      // console.error("Modal is open but subscriptionPk is missing!");
    }
  }, [isModalOpen, subscriptionPk, currency, JWT, planPrice, appliedCoupon]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Separate useEffect for email with throttling
  useEffect(() => {
    // Clear previous timeout
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    // Only proceed if email is not empty and subscriptionPk exists
    if (email && subscriptionPk) {
      // Validate email format
      if (isValidEmail(email)) {
        // Set new timeout for throttling (500ms delay)
        emailTimeoutRef.current = setTimeout(() => {
          createPaymentIntent();
        }, 500);
      }
    }

    // Cleanup timeout on unmount or when email changes again
    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, [email]);

  // Function to create payment intent
  const createPaymentIntent = async () => {
    setMsg(null);
    setClientSecret(null);
    setPiId(null);

    try {
      // Ensure the API endpoint URL is correctly formatted
      const apiUrl = `${API_BASE}/payments/stripe/create-intent`;

      // Use a more robust fetch with better error handling
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${JWT}`,
          "Content-Type": "application/json",
          "Idempotency-Key":
            (typeof self !== "undefined" &&
              self.crypto &&
              typeof self.crypto.randomUUID === "function" &&
              self.crypto.randomUUID()) ||
            String(Date.now()),
        },
        body: JSON.stringify({
          subscription_pk: subscriptionPk,
          coupon_code: appliedCoupon ? appliedCoupon.code : null,
          currency,
          customer_email: email,
        }),
      }).catch((error) => {
        // console.error("Fetch error:", error);
        throw error;
      });

      const res = await response.json();

      if (!res?.success) {
        // console.log("herer", res?.message || res?.data?.error);
        setMsg(res?.message || res?.data?.error || "Could not create payment");
        return;
      }
      setClientSecret(res.data.client_secret);
      setPiId(res.data.payment_intent_id);

      // Extract amount from the response if available
      // Check different possible locations for the amount in the response
      if (res.data.amount) {
        setAmount(res.data.amount / 100); // Convert from cents to dollars
      } else if (res.data.payment_intent && res.data.payment_intent.amount) {
        setAmount(res.data.payment_intent.amount / 100);
      }
      // else {
      //     // If amount is not in the response, fetch it from the payment intent
      //     try {
      //         const piResponse = await fetch(`${API_BASE}/payments/stripe/payment-intent/${res.data.payment_intent_id}`, {
      //             method: "GET",
      //             headers: { Authorization: `Bearer ${JWT}`, "Content-Type": "application/json" },
      //         });
      //         const piData = await piResponse.json();
      //         if (piData.success && piData.data && piData.data.amount) {
      //             setAmount(piData.data.amount / 100);
      //         }
      //     } catch (err) {
      //         console.error("Error fetching payment intent details:", err);
      //     }
      // }
    } catch (e) {
      // console.error("Error creating payment intent:", e);
      setMsg(`Error creating payment intent: ${e.message || "Unknown error"}`);
      // Log additional details about the error
    }
  };

  // 2) Flow A: confirm on client with Elements
  const pay = async () => {
    if (!stripe || !elements || !clientSecret) return;
    await createPaymentIntent();
    setLoading(true);
    setMsg(null);

    const cardEl = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardEl,
          billing_details: { email },
        },
      },
    );

    if (error) {
      setMsg(error.message || "Payment failed");
      setLoading(false);
      return;
    }
    if (paymentIntent?.status !== "succeeded") {
      setMsg(`Status: ${paymentIntent?.status}`);
      setLoading(false);
      return;
    }
    await finalize(paymentIntent.id);
  };

  // 2b) Flow B (optional): create PM, then server confirm-with-pm
  const payServerConfirm = async () => {
    if (!stripe || !elements || !piId) return;
    setLoading(true);
    setMsg(null);

    const cardEl = elements.getElement(CardElement);
    const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardEl,
      billing_details: { email },
    });
    if (pmErr || !paymentMethod) {
      setMsg(pmErr?.message || "PM failed");
      setLoading(false);
      return;
    }

    const res = await fetch(`${API_BASE}/payments/stripe/confirm-with-pm`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${JWT}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payment_intent_id: piId,
        payment_method_id: paymentMethod.id,
      }),
    }).then((r) => r.json());

    if (!res?.success) {
      setMsg(res?.message || res?.data?.error || "Server confirm failed");
      setLoading(false);
      return;
    }

    if (res.data.status === "requires_action" && clientSecret) {
      // complete 3DS in browser
      const { error, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret);
      if (error || paymentIntent?.status !== "succeeded") {
        setMsg(error?.message || `Status: ${paymentIntent?.status}`);
        setLoading(false);
        return;
      }
      await finalize(paymentIntent.id);
    } else if (res.data.status === "succeeded") {
      await finalize(piId);
    } else {
      setMsg(`Unexpected status: ${res.data.status}`);
      setLoading(false);
    }
  };

  // 3) Finalize: credit & persist
  const finalize = async (paymentIntentId) => {
    const res = await fetch(`${API_BASE}/payments/stripe/confirm`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${JWT}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    }).then((r) => r.json());
    setLoading(false);
    setMsg(
      res?.success ? "✅ Payment complete" : res?.message || "Finalize failed",
    );
    if (res?.success) {
      navigate("/payment-success");
    } else {
      navigate("/payment-failure");
    }
  };

  const validateCoupon = async (couponCode) => {
    if (!couponCode || couponCode.trim() === "") {
      setCouponMessage("Please enter a coupon code");
      setCouponError(true);
      setTimeout(() => {
        setCouponMessage("");
        setCouponError(false);
      }, 3000);
      return;
    }

    setIsApplyingCoupon(true);
    setCouponMessage("");
    setCouponError(false);

    const token = localStorage.getItem("token");
    try {
      const payload = {
        subscription_pk: subscriptionPk,
        coupon_code: couponCodeInput,
        currency: currency,
        // code: couponCode,
        // amount: selectedPlan.price,
      };
      const response = await dispatch(validateCouponCode(payload)).unwrap();
      // console.log("res", response);

      if (response.success) {
        // Calculate discounted price in original currency units
        // await createPaymentIntent();
        const finalAmountMinor = response.data.final_amount_minor;
        let discountedAmount;

        discountedAmount = finalAmountMinor / 100;
        setAmount(discountedAmount); // Update displayed amount to discounted price

        setDiscountedPrice(discountedAmount);
        setAppliedCoupon({
          code: couponCodeInput,
          discount: response.data.discount_amount_minor / 100,
          originalAmount: planPrice,
        });
        setCouponMessage(
          `Coupon applied! You saved ${response.data.discount_amount_minor / 100} $`,
        );
        setCouponError(false);

        // Clear message after 3 seconds
        // setTimeout(() => {
        //   setCouponMessage("");
        // }, 3000);
      } else {
        setCouponMessage(response.message || "Invalid coupon code");
        setCouponError(true);
        // setTimeout(() => {
        //   setCouponMessage("");
        //   setCouponError(false);
        // }, 3000);
      }
    } catch (error) {
      // console.error("Error validating coupon:", error);
      setCouponMessage(
        error.response?.data?.message || "Error applying coupon",
      );
      setCouponError(true);
      setTimeout(() => {
        setCouponMessage("");
        setCouponError(false);
      }, 3000);
    } finally {
      setIsApplyingCoupon(false);
    }
  };
  const removeCoupon = async () => {
  setCouponCodeInput("");
  setAppliedCoupon(null);
  setDiscountedPrice(null);
  setAmount(planPrice);

  await createPaymentIntent();

  setCouponMessage("Coupon removed");
  setCouponError(false);

  setTimeout(() => {
    setCouponMessage("");
  }, 3000);
};

return (
    <div className="">
      <div className="mb-4 p-4 bg-gray-50 rounded-md border border-gray-200 text-center">
        <p className="text-sm text-gray-500 mb-1">Payment Amount</p>
        {amount ? (
          <p className="text-2xl font-bold">
            $ {amount} {currency.toUpperCase()}
          </p>
        ) : (
          <p className="text-2xl font-bold">Loading...</p>
        )}
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Email</label>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-2 border rounded-md mb-4"
        />
        <div className="border rounded-md p-4 bg-white">
          <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
        </div>
        {msg && <p className="mt-2 text-sm text-red-500">{msg}</p>}

        <div className="mt-3">
          <label
            htmlFor="couponCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Coupon Code (if any)
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                id="couponCode"
                type="text"
                placeholder="Enter coupon code"
                value={couponCodeInput}
                onChange={(e) => setCouponCodeInput(e.target.value.toUpperCase())}
                disabled={appliedCoupon !== null}
                className={`w-full p-3 max-md:p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black transition-all ${
                  appliedCoupon ? "bg-gray-100 text-gray-500" : ""
                }`}
              />
              {appliedCoupon && (
                <button
                  type="button"
                  onClick={removeCoupon}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => validateCoupon(couponCodeInput)}
              disabled={isApplyingCoupon || appliedCoupon !== null}
              className="px-6 py-3 max-md:px-4 max-md:py-2 bg-gray-800 text-white rounded-md font-medium hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isApplyingCoupon ? (
                <>
                  <FiLoader className="animate-spin text-white" />
                  <span>Applying...</span>
                </>
              ) : (
                "Apply"
              )}
            </button>
          </div>
          {couponMessage && (
            <p
              className={`text-sm mt-1 ${couponError ? "text-red-500" : "text-green-500"}`}
            >
              {couponMessage}
            </p>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <Button
          variant="outline"
          onClick={() => setIsModalOpen(false)}
          disabled={loading}
          className="px-6 min-w-[100px] h-10"
        >
          Cancel
        </Button>

        <Button
          onClick={pay}
          // disabled={loading || !stripe || !clientSecret || !elements}
          className="px-6 min-w-[100px] h-10 main-button  text-white"
        >
          {loading ? "Processing..." : "Pay"}
        </Button>
      </div>
    </div>
  );
}

export default function CheckoutPage({ subscriptionPk, currency, planPrice }) {
  // e.g., from router params

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Elements stripe={stripePromise}>
      <Checkout
        subscriptionPk={subscriptionPk}
        currency={currency}
        planPrice={Number(planPrice)}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </Elements>
  );
}
